/**
 * Card inline de origem de anúncio (Click-to-WhatsApp) no corpo do chat
 * Exibe a origem da conversa quando lead chega via tráfego pago (Meta Ads)
 * Similar ao PostCardBubble para comentários do Instagram
 * 
 * @version 1.0.0
 * @date 2026-02-07
 */

import { useState } from 'react';
import { ExternalLink, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Interface de props do componente
export interface AdSourceCardBubbleProps {
  /** Título do anúncio */
  adTitle?: string | null;
  /** Texto/corpo do anúncio */
  adBody?: string | null;
  /** URL da thumbnail do anúncio */
  thumbnailUrl?: string | null;
  /** Link direto para o anúncio */
  sourceUrl?: string | null;
  /** Plataforma de origem (instagram ou facebook) */
  sourceApp?: string | null;
  /** Data/hora da mensagem */
  timestamp: string;
  /** Classes CSS adicionais */
  className?: string;
  /** Ref para scroll */
  innerRef?: React.Ref<HTMLDivElement>;
}

/**
 * Componente que exibe card de origem de anúncio Meta Ads no chat
 * Aparece quando uma conversa é iniciada via Click-to-WhatsApp (CTWA)
 */
export function AdSourceCardBubble({
  adTitle,
  adBody,
  thumbnailUrl,
  sourceUrl,
  sourceApp,
  timestamp,
  className,
  innerRef,
}: AdSourceCardBubbleProps) {
  const [imageError, setImageError] = useState(false);
  
  // Verifica se tem link válido para o anúncio
  const hasValidLink = !!sourceUrl;
  
  // Handler para abrir anúncio
  const handleOpenAd = () => {
    if (sourceUrl) {
      window.open(sourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Formata a data/hora
  const formattedTime = format(new Date(timestamp), 'HH:mm', { locale: ptBR });
  
  // Texto a exibir (prioriza título, fallback para body)
  const displayText = adTitle || adBody || 'Anúncio Meta Ads';
  
  // Label da plataforma de origem (Instagram ou Facebook)
  const platformLabel = sourceApp === 'instagram' 
    ? 'Anúncio do Instagram' 
    : sourceApp === 'facebook' 
      ? 'Anúncio do Facebook' 
      : 'Meta Ads';
  return (
    <div
      ref={innerRef}
      className={cn(
        'flex justify-center my-3',
        className
      )}
    >
      {/* Card do anúncio com visual diferenciado - cores Meta/Facebook */}
      <div className={cn(
        'relative flex items-center gap-3 p-3 rounded-xl border',
        'bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20',
        'border-blue-200/50 dark:border-blue-800/30',
        'max-w-[90%] md:max-w-[60%]'
      )}>
        {/* Badge com plataforma de origem - gradiente azul Meta */}
        <div className="absolute -top-2 left-3 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gradient-to-r from-blue-600 to-blue-500 text-white flex items-center gap-1">
          <Megaphone className="h-2.5 w-2.5" />
          {platformLabel}
        </div>

        {/* Thumbnail do anúncio */}
        <div className="relative h-16 w-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 mt-1">
          {thumbnailUrl && !imageError ? (
            <img
              src={thumbnailUrl}
              alt="Thumbnail do anúncio"
              className="h-full w-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30">
              <Megaphone className="h-8 w-8 text-blue-500/60" />
            </div>
          )}
        </div>

        {/* Informações do anúncio */}
        <div className="flex-1 min-w-0 mt-1">
          <p className="text-sm font-medium text-foreground">
            {platformLabel}
          </p>
          
          {/* Título/descrição do anúncio */}
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            "{displayText}"
          </p>
          
          {/* Hora e link */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] text-muted-foreground">
              {formattedTime}
            </span>
            
            {hasValidLink ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px] gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                onClick={handleOpenAd}
              >
                <ExternalLink className="h-3 w-3" />
                Ver anúncio
              </Button>
            ) : (
              <span className="text-[10px] text-muted-foreground/60 italic">
                Link indisponível
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdSourceCardBubble;
